import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  min-height: 100vh;
  padding-top: 6rem;
  position: relative;
`;


const Section = styled.section`
  position: relative;
  padding: 4rem 1.5rem;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  gap: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
  margin: 0 auto;
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const SectionTitle = styled.h1`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`;

const SectionCopy = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.inputText};
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.inputText};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const VideoCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoDuration = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const VideoInfo = styled.div`
  padding: 1.5rem;
`;

const VideoTitle = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const VideoDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.95rem;
`;

const VideoMeta = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const CategoryBadge = styled.span<{ category: string }>`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ category }) => {
    switch (category) {
      case 'Workshop': return '#E0F2FE';
      case 'Testimonial': return '#F0FDF4';
      case 'Educational': return '#FEF3C7';
      case 'Community': return '#F3E8FF';
      default: return '#F1F5F9';
    }
  }};
  color: ${({ category }) => {
    switch (category) {
      case 'Workshop': return '#0C4A6E';
      case 'Testimonial': return '#166534';
      case 'Educational': return '#92400E';
      case 'Community': return '#6B21A8';
      default: return '#475569';
    }
  }};
`;

const VideoActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  align-items: center;
`;

const WatchButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const ShareButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0.6rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const LoadMoreButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 3rem auto 0;
  display: block;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

interface Video {
  id: string;
  title: string;
  description: string;
  duration: string;
  category: 'Workshop' | 'Testimonial' | 'Educational' | 'Community';
  embedId: string;
  uploadDate: string;
  featured?: boolean;
}

const VideoLibrary: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [filteredVideos, setFilteredVideos] = useState<Video[]>(videos);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [displayCount, setDisplayCount] = useState<number>(6);

  // Load videos from API
  React.useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await fetch('/api/videos.php');
        const data = await response.json();
        
        if (data.success && data.videos) {
          const formattedVideos = data.videos.map((video: any) => ({
            id: video.id,
            title: video.title,
            description: video.description,
            duration: video.duration || '0:00',
            category: video.category,
            embedId: video.embedId,
            uploadDate: video.uploadDate,
            featured: video.tier_order === 1
          }));
          setVideos(formattedVideos);
        }
      } catch (error) {
        console.error('Error loading videos:', error);
        // Fallback to empty array on error
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  // Filter videos based on category and search
  React.useEffect(() => {
    let filtered = videos;

    if (categoryFilter !== 'All') {
      filtered = filtered.filter(video => video.category === categoryFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(video => 
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredVideos(filtered);
  }, [categoryFilter, searchTerm, videos]);

  const handleWatch = (video: Video) => {
    // In a real app, this might open a modal or navigate to a video page
    window.open(`https://www.youtube.com/watch?v=${video.embedId}`, '_blank');
  };

  const handleShare = (video: Video) => {
    if (navigator.share) {
      navigator.share({
        title: video.title,
        text: video.description,
        url: `https://www.youtube.com/watch?v=${video.embedId}`
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(`${video.title} - https://www.youtube.com/watch?v=${video.embedId}`);
      alert('Video link copied to clipboard!');
    }
  };

  const loadMore = () => {
    setDisplayCount(prev => prev + 6);
  };

  const hasMoreVideos = displayCount < filteredVideos.length;

  return (
    <PageWrapper>
      <Section>
        <SectionInner>
          <BackButton to="/#videos">
            ← Back to Home
          </BackButton>
          
          <SectionHeader>
            <Eyebrow>Complete Video Library</Eyebrow>
            <SectionTitle>Emotional Wellbeing Resources</SectionTitle>
            <SectionCopy>
              Explore our complete collection of videos covering stress management, emotional resilience, 
              community stories, and practical tools for mental wellbeing. New content added regularly.
            </SectionCopy>
          </SectionHeader>


          <FilterSection>
            <FilterLabel>Filter by:</FilterLabel>
            <FilterSelect 
              value={categoryFilter} 
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All Categories</option>
              <option value="Workshop">Workshops</option>
              <option value="Educational">Educational</option>
              <option value="Testimonial">Testimonials</option>
              <option value="Community">Community</option>
            </FilterSelect>

            <SearchInput
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterSection>

          <div style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1rem' }}>
            Showing {Math.min(displayCount, filteredVideos.length)} of {filteredVideos.length} videos
          </div>

          <VideoGrid>
            {filteredVideos.slice(0, displayCount).map((video) => (
              <VideoCard key={video.id}>
                <VideoThumbnail>
                  <VideoIframe
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <VideoDuration>{video.duration}</VideoDuration>
                </VideoThumbnail>
                
                <VideoInfo>
                  <VideoTitle>{video.title}</VideoTitle>
                  <VideoDescription>{video.description}</VideoDescription>
                  
                  <VideoMeta>
                    <CategoryBadge category={video.category}>{video.category}</CategoryBadge>
                    <span>•</span>
                    <span>{new Date(video.uploadDate).toLocaleDateString()}</span>
                    {video.featured && (
                      <>
                        <span>•</span>
                        <span style={{ color: '#10B981', fontWeight: '600' }}>⭐ Featured</span>
                      </>
                    )}
                  </VideoMeta>
                  
                  <VideoActions>
                    <WatchButton onClick={() => handleWatch(video)}>
                      ▶ Watch Now
                    </WatchButton>
                    <ShareButton onClick={() => handleShare(video)}>
                      📤
                    </ShareButton>
                  </VideoActions>
                </VideoInfo>
              </VideoCard>
            ))}
          </VideoGrid>

          {hasMoreVideos && (
            <LoadMoreButton onClick={loadMore}>
              Load More Videos
            </LoadMoreButton>
          )}
        </SectionInner>
      </Section>
    </PageWrapper>
  );
};

export default VideoLibrary;